<script setup lang="ts">
import type { ContentNode } from "#layers/books/types";

const props = defineProps<{
  bookId: string;
  bookTitle: string;
  tree: ContentNode[];
  currentPath: string;
}>();

// Load persisted expanded state from localStorage
const storageKey = computed(() => `book-tree-state-${props.bookId}`);

const expandedNodes = ref<Set<string>>(new Set());

// Initialize expanded state from localStorage
onMounted(() => {
  try {
    const stored = localStorage.getItem(storageKey.value);
    if (stored) {
      expandedNodes.value = new Set(JSON.parse(stored));
    } else {
      // Auto-expand path to current content
      expandToPath(props.currentPath);
    }
  } catch {
    // Ignore localStorage errors
  }
});

// Watch for path changes and expand to current
watch(
  () => props.currentPath,
  (newPath) => {
    expandToPath(newPath);
  },
);

// Persist expanded state
watch(
  expandedNodes,
  (nodes) => {
    try {
      localStorage.setItem(storageKey.value, JSON.stringify([...nodes]));
    } catch {
      // Ignore localStorage errors
    }
  },
  { deep: true },
);

function expandToPath(path: string) {
  if (!path) return;

  const parts = path.split("/");
  let accumulated = "";

  for (const part of parts.slice(0, -1)) {
    accumulated += (accumulated ? "/" : "") + part;
    expandedNodes.value.add(accumulated);
  }
}

function toggleNode(nodeId: string) {
  if (expandedNodes.value.has(nodeId)) {
    expandedNodes.value.delete(nodeId);
  } else {
    expandedNodes.value.add(nodeId);
  }
  // Trigger reactivity
  expandedNodes.value = new Set(expandedNodes.value);
}
</script>

<template>
  <div class="h-full flex flex-col">
    <!-- Book title header -->
    <div class="p-4 border-b border-gray-200 dark:border-gray-700">
      <NuxtLink
        :to="`/book/${bookId}`"
        class="font-semibold text-gray-900 dark:text-white hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
      >
        {{ bookTitle }}
      </NuxtLink>
    </div>

    <!-- Tree content -->
    <div class="flex-1 overflow-y-auto p-2">
      <div v-if="tree.length === 0" class="text-center py-8 text-gray-500">
        No content found
      </div>
      <BookTreeNode
        v-for="node in tree"
        :key="node.id"
        :node="node"
        :current-path="currentPath"
        :expanded-nodes="expandedNodes"
        @toggle="toggleNode"
      />
    </div>
  </div>
</template>
